import {
  CreateButton,
  DateField,
  DeleteButton,
  List,
  RefreshButton,
  TextField,
  getDefaultSortOrder,
  useTable,
} from "@refinedev/antd";

import { Col, Divider, Row, Space, Table, Tag, Button, notification } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IResourceComponentsProps, useModal, useInvalidate, useGetIdentity } from "@refinedev/core";

import { IVesselDataTransfer } from "interfaces";
import { RoleEnum } from "types/roles";
import prettyBytes from "pretty-bytes";
import React from "react";
import { DownloadVesselDataTransfer } from "./components/DownloadVesselDataTransfer";
import { CopyOutlined } from "@ant-design/icons";

export const VesselDataTransferList: React.FC<
  IResourceComponentsProps
> = () => {
  const [record, setRecord] = React.useState<IVesselDataTransfer>();
  const invalidate = useInvalidate();
  const { data: identity } = useGetIdentity();
  const userRole = identity?.role as RoleEnum;
  
  const canCreateRequest = userRole === RoleEnum.DistributorAdmin || 
                          userRole === RoleEnum.DistributorSuperAdmin;

  const { tableProps, sorters, tableQuery: { isLoading, refetch }, searchFormProps, filters } =
    useTable<IVesselDataTransfer>({
      resource: "vessel-data-transfer",
      syncWithLocation: false,

      sorters: {
        initial: [
          {
            field: "id",
            order: "desc",
          },
        ]
      }
    });
  const { show, visible, close } = useModal();

  const handleRefresh = () => {
    invalidate({
      resource: "vessel-data-transfer",
      invalidates: ["list"],
    });
  };

  const columns: ColumnsType<IVesselDataTransfer> = [
    {
      title: "IMO Number",
      dataIndex: "imoNumber",
      key: "imoNumber",
      width: 150,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("imoNumber", sorters),
      sorter: true,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("title", sorters),
      sorter: true,
    },
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("fileName", sorters),
      sorter: true,
    },
    {
      title: "File Size",
      dataIndex: "fileSize",
      key: "fileSize",
      width: 150,
      className: "wrap-text",
      render: (value) => <TextField value={value ? prettyBytes(Number(value)) : null} />,
      defaultSortOrder: getDefaultSortOrder("fileSize", sorters),
      sorter: true,
    },
    {
      title: "Last Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 200,
      className: "wrap-text",
      render: (value) => (
        <DateField format="DD-MMM-YYYY hh:mm:ss A" value={value} />
      ),
      defaultSortOrder: getDefaultSortOrder("updatedAt", sorters),
      sorter: true,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <DownloadVesselDataTransfer
            storageUrl={record.storageUrl}
            fileName={record.fileName}
          />
          <Button
            icon={<CopyOutlined />}
            size="small"
            onClick={() => {
              navigator.clipboard.writeText(record.storageUrl);
              notification.success({
                message: "Success",
                description: "Download link copied to clipboard",
                placement: "topRight",
              });
            }}
          />
          <DeleteButton
            hideText
            size="small"
            recordItemId={record.id}
            successNotification={{
              type: "success",
              message: "Deleted the requested data file",
              description: "Deleted Successfully"
            }}
            errorNotification={{
              type: "error",
              message: "Could not delete the requested data file. Please try again.",
              description: "Failed to Delete"
            }}
            onSuccess={() => {
              // TODO: Delete the file from cloud storage bucket
            }}
          />
        </Space>
      ),
    },
  ];

  return (
      <List
        breadcrumb={false}
        headerProps={{
          // onBack: () => window.history.back(),
          title: "Vessel Data Transfer",
          subTitle: "data requests from vessels",
          tags: isLoading ? (
            <Tag color="orange">loading</Tag>
          ) : (
            <Tag color="green">live</Tag>
          ),
          extra: (
            <React.Fragment>
              <Space>
                <RefreshButton 
                  resource="vessel-data-transfer"
                  onClick={handleRefresh}
                  loading={isLoading}
                />
                <CreateButton 
                  children="Request Data" 
                  disabled={!canCreateRequest}
                />
              </Space>
            </React.Fragment>
          ),
          ghost: true,
          footer: (
            <Divider plain>
              © {new Date().getFullYear()} e-Mariner NavSolutions
            </Divider>
          ),
        }}
      >
        <Row gutter={[16, 16]}>
          <Col xl={24} xs={24}>
            <Table
              {...tableProps}
              columns={columns}
              rowKey="id"
              size="middle"
              scroll={{ x: 1200 }}
              sticky
              locale={{
                emptyText: 'No files found. Request data to see details here.'
              }}
              pagination={{
                ...tableProps.pagination,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              }}
            />
          </Col>
        </Row>
      </List>
  );
};